import { useState, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Layout } from '../components/Layout';
import { useAuth } from '../context/AuthContext';
import api from '../lib/api';
import { fuelApi } from '../lib/api';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  Fuel, MapPin, Loader2, Navigation, Search,
  Clock, Filter, DollarSign, TrendingDown, Flag, Star, Trash2
} from 'lucide-react';
import { toast } from 'sonner';
import { FuelStationsMap } from '../components/FuelStationsMap';

export default function StazioniBenzina() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('stations');
  const [stations, setStations] = useState([]);
  const [priceStations, setPriceStations] = useState([]);
  const [favoriteStations, setFavoriteStations] = useState([]);
  const [favoriteIds, setFavoriteIds] = useState(new Set());
  const [loading, setLoading] = useState(false);
  const [loadingPrices, setLoadingPrices] = useState(false);
  const [loadingFavorites, setLoadingFavorites] = useState(false);
  const [userLocation, setUserLocation] = useState(null);
  const [gettingLocation, setGettingLocation] = useState(false);
  const [selectedStation, setSelectedStation] = useState(null);
  const [radius, setRadius] = useState(5000);
  const [filterFuelType, setFilterFuelType] = useState('all');
  const [searchPerformed, setSearchPerformed] = useState(false);
  const [priceSearchPerformed, setPriceSearchPerformed] = useState(false);
  const [priceSources, setPriceSources] = useState(null);

  // Load favorites on mount
  useEffect(() => {
    loadFavorites();
  }, []);

  const loadFavorites = async () => {
    setLoadingFavorites(true);
    try {
      const response = await fuelApi.getFavorites();
      setFavoriteStations(response.data);
      setFavoriteIds(new Set(response.data.map(s => s.station_id)));
    } catch (error) {
      console.error('Error loading favorites:', error);
    } finally {
      setLoadingFavorites(false);
    }
  };

  // Toggle favorite
  const toggleFavorite = async (station, source = 'OSM') => {
    const stationId = station.id;
    const isFavorite = favoriteIds.has(stationId);

    try {
      if (isFavorite) {
        await fuelApi.removeFavorite(stationId);
        setFavoriteIds(prev => {
          const newSet = new Set(prev);
          newSet.delete(stationId);
          return newSet;
        });
        setFavoriteStations(prev => prev.filter(s => s.station_id !== stationId));
        toast.success(t('fuelStations.removedFromFavorites') || 'Rimosso dai preferiti');
      } else {
        await fuelApi.addFavorite({
          station_id: stationId,
          name: station.name,
          brand: station.brand,
          lat: station.lat,
          lon: station.lon,
          address: station.address,
          city: station.city,
          country: source
        });
        setFavoriteIds(prev => new Set([...prev, stationId]));
        loadFavorites(); // Reload to get full data
        toast.success(t('fuelStations.addedToFavorites') || 'Aggiunto ai preferiti');
      }
    } catch (error) {
      console.error('Error toggling favorite:', error);
      toast.error(t('errors.saveError'));
    }
  };

  // Get user location
  const getUserLocation = useCallback(() => {
    setGettingLocation(true);
    if (!navigator.geolocation) {
      toast.error(t('fuelStations.geoNotSupported') || 'Geolocalizzazione non supportata');
      setGettingLocation(false);
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        const loc = {
          lat: position.coords.latitude,
          lon: position.coords.longitude
        };
        setUserLocation(loc);
        setGettingLocation(false);
        toast.success(t('fuelStations.locationFound') || 'Posizione trovata');
      },
      (error) => {
        console.error('Geolocation error:', error);
        toast.error(t('fuelStations.locationError') || 'Errore nel rilevare la posizione');
        setGettingLocation(false);
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 60000 }
    );
  }, [t]);

  // Search for fuel stations (OpenStreetMap)
  const searchStations = async () => {
    if (!userLocation) {
      toast.error(t('fuelStations.needLocation') || 'Imposta prima la tua posizione');
      return;
    }

    setLoading(true);
    setSearchPerformed(true);
    try {
      const response = await api.post('/fuel-stations/search', {
        lat: userLocation.lat,
        lon: userLocation.lon,
        radius: radius
      });
      setStations(response.data.stations || []);
      if (response.data.count === 0) {
        toast.info(t('fuelStations.noStationsFound') || 'Nessuna stazione trovata nel raggio selezionato');
      } else {
        toast.success(`${response.data.count} ${t('fuelStations.stationsFound') || 'stazioni trovate'}`);
      }
    } catch (error) {
      console.error('Search error:', error);
      toast.error(t('fuelStations.searchError') || 'Errore nella ricerca');
    } finally {
      setLoading(false);
    }
  };

  // Search for fuel prices (DE, FR, IT)
  const searchPrices = async () => {
    if (!userLocation) {
      toast.error(t('fuelStations.needLocation') || 'Imposta prima la tua posizione');
      return;
    }

    setLoadingPrices(true);
    setPriceSearchPerformed(true);
    try {
      const response = await fuelApi.searchPrices(userLocation.lat, userLocation.lon, radius);
      setPriceStations(response.data.stations || []);
      setPriceSources(response.data.sources);
      
      if (response.data.count === 0) {
        toast.info(t('fuelStations.noPricesFound') || 'Nessun prezzo trovato. Disponibile solo per Germania, Francia e Italia.');
      } else {
        toast.success(`${response.data.count} ${t('fuelStations.stationsWithPrices') || 'stazioni con prezzi trovate'}`);
      }
    } catch (error) {
      console.error('Price search error:', error);
      toast.error(t('fuelStations.priceSearchError') || 'Errore nella ricerca prezzi');
    } finally {
      setLoadingPrices(false);
    }
  };

  // Format price
  const formatPrice = (price, currency = '€') => {
    if (!price) return '—';
    return `${currency} ${price.toFixed(3)}`;
  };

  // Get country flag emoji
  const getCountryFlag = (country) => {
    const flags = { DE: '🇩🇪', FR: '🇫🇷', IT: '🇮🇹' };
    return flags[country] || '🏳️';
  };

  // Format distance
  const formatDistance = (meters) => {
    if (!meters) return '—';
    if (meters < 1000) return `${Math.round(meters)} m`;
    return `${(meters / 1000).toFixed(1)} km`;
  };

  // Filter stations by fuel type
  const filteredStations = filterFuelType === 'all' 
    ? stations 
    : stations.filter(s => s.fuel_types?.some(ft => 
        ft.toLowerCase().includes(filterFuelType.toLowerCase())
      ));

  // Navigate to station
  const navigateToStation = (station) => {
    const url = `https://www.google.com/maps/dir/?api=1&destination=${station.lat},${station.lon}`;
    window.open(url, '_blank');
  };

  return (
    <Layout>
      <div className="p-6 md:p-8 max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-2 flex items-center gap-3">
            <Fuel className="w-10 h-10 text-[#FF5F1F]" />
            {t('fuelStations.title') || 'Stazioni Benzina'}
          </h1>
          <p className="text-[#9CA3AF] uppercase tracking-wider text-sm">
            {t('fuelStations.subtitle') || 'Trova le stazioni di rifornimento più vicine'}
          </p>
        </div>

        {/* Search Controls */}
        <div className="bg-[#121212] border border-[#262626] p-6 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 items-end">
            {/* Location */}
            <div className="md:col-span-1">
              <Label className="text-[#9CA3AF] mb-2 block">
                {t('fuelStations.yourLocation') || 'La tua posizione'}
              </Label>
              <Button
                onClick={getUserLocation}
                disabled={gettingLocation}
                variant="outline"
                className="w-full border-[#262626] hover:border-[#FF5F1F] hover:text-[#FF5F1F]"
                data-testid="get-location-btn"
              >
                {gettingLocation ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <MapPin className="w-4 h-4 mr-2" />
                )}
                {userLocation 
                  ? `${userLocation.lat.toFixed(4)}, ${userLocation.lon.toFixed(4)}`
                  : (t('fuelStations.getLocation') || 'Rileva posizione')}
              </Button>
            </div>

            {/* Radius */}
            <div className="md:col-span-1">
              <Label className="text-[#9CA3AF] mb-2 block">
                {t('fuelStations.searchRadius') || 'Raggio di ricerca'}
              </Label>
              <Select value={radius.toString()} onValueChange={(v) => setRadius(parseInt(v))}>
                <SelectTrigger className="bg-[#0a0a0a] border-[#262626]" data-testid="radius-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="2000">2 km</SelectItem>
                  <SelectItem value="5000">5 km</SelectItem>
                  <SelectItem value="10000">10 km</SelectItem>
                  <SelectItem value="20000">20 km</SelectItem>
                  <SelectItem value="50000">50 km</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Fuel Type Filter */}
            <div className="md:col-span-1">
              <Label className="text-[#9CA3AF] mb-2 block">
                {t('fuelStations.fuelType') || 'Tipo carburante'}
              </Label>
              <Select value={filterFuelType} onValueChange={setFilterFuelType}>
                <SelectTrigger className="bg-[#0a0a0a] border-[#262626]" data-testid="fuel-filter">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t('fuelStations.allFuels') || 'Tutti'}</SelectItem>
                  <SelectItem value="benzina">Benzina</SelectItem>
                  <SelectItem value="diesel">Diesel</SelectItem>
                  <SelectItem value="gpl">GPL</SelectItem>
                  <SelectItem value="metano">Metano</SelectItem>
                  <SelectItem value="elettrico">Elettrico</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Search Button */}
            <div className="md:col-span-1">
              <Button
                onClick={searchStations}
                disabled={loading || !userLocation}
                className="w-full bg-[#FF5F1F] hover:bg-[#FF5F1F]/90 text-white"
                data-testid="search-stations-btn"
              >
                {loading ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Search className="w-4 h-4 mr-2" />
                )}
                {t('fuelStations.search') || 'Cerca Stazioni'}
              </Button>
            </div>
          </div>
        </div>

        {/* Tabs: Stations, Prices & Favorites */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="bg-[#121212] border border-[#262626] p-1 mb-6">
            <TabsTrigger 
              value="stations" 
              className="data-[state=active]:bg-[#FF5F1F] data-[state=active]:text-white flex items-center gap-2"
            >
              <MapPin className="w-4 h-4" />
              {t('fuelStations.tabStations') || 'Stazioni'}
            </TabsTrigger>
            <TabsTrigger 
              value="prices" 
              className="data-[state=active]:bg-[#FF5F1F] data-[state=active]:text-white flex items-center gap-2"
            >
              <DollarSign className="w-4 h-4" />
              {t('fuelStations.tabPrices') || 'Prezzi'}
            </TabsTrigger>
            <TabsTrigger 
              value="favorites" 
              className="data-[state=active]:bg-[#FF5F1F] data-[state=active]:text-white flex items-center gap-2"
            >
              <Star className="w-4 h-4" />
              {t('fuelStations.tabFavorites') || 'Preferiti'}
              {favoriteStations.length > 0 && (
                <span className="ml-1 px-1.5 py-0.5 bg-[#262626] text-xs rounded-full">
                  {favoriteStations.length}
                </span>
              )}
            </TabsTrigger>
          </TabsList>

          {/* Tab: Stations (OpenStreetMap) */}
          <TabsContent value="stations">
            <div className="flex justify-end mb-4">
              <Button
                onClick={searchStations}
                disabled={loading || !userLocation}
                className="bg-[#FF5F1F] hover:bg-[#FF5F1F]/90 text-white"
                data-testid="search-stations-btn"
              >
                {loading ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Search className="w-4 h-4 mr-2" />
                )}
                {t('fuelStations.search') || 'Cerca Stazioni'}
              </Button>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 md:gap-6">
              {/* Map */}
              <div className="lg:col-span-2 bg-[#121212] border border-[#262626] overflow-hidden order-2 lg:order-1">
                <FuelStationsMap
                  stations={filteredStations}
                  userLocation={userLocation}
                  radius={radius}
                  selectedStation={selectedStation}
                  onStationSelect={setSelectedStation}
                />
              </div>

              {/* Station List */}
              <div className="lg:col-span-1 bg-[#121212] border border-[#262626] order-1 lg:order-2">
                <div className="p-4 border-b border-[#262626] flex items-center justify-between">
                  <h2 className="text-lg font-semibold text-white flex items-center gap-2">
                    <Filter className="w-5 h-5 text-[#FF5F1F]" />
                    {t('fuelStations.results') || 'Risultati'}
                  </h2>
                  <span className="text-sm text-[#9CA3AF]">
                    {filteredStations.length} {t('fuelStations.stations') || 'stazioni'}
                  </span>
                </div>

                <div className="max-h-[250px] lg:max-h-[350px] overflow-y-auto">
                  {!searchPerformed ? (
                    <div className="p-8 text-center text-[#9CA3AF]">
                      <MapPin className="w-12 h-12 mx-auto mb-4 opacity-50" />
                      <p>{t('fuelStations.searchPrompt') || 'Rileva la tua posizione e cerca le stazioni vicine'}</p>
                    </div>
                  ) : loading ? (
                    <div className="p-8 text-center">
                      <Loader2 className="w-8 h-8 mx-auto animate-spin text-[#FF5F1F]" />
                      <p className="mt-2 text-[#9CA3AF]">{t('fuelStations.searching') || 'Ricerca in corso...'}</p>
                    </div>
                  ) : filteredStations.length === 0 ? (
                    <div className="p-8 text-center text-[#9CA3AF]">
                      <Fuel className="w-12 h-12 mx-auto mb-4 opacity-50" />
                      <p>{t('fuelStations.noResults') || 'Nessuna stazione trovata'}</p>
                      <p className="text-sm mt-2">{t('fuelStations.tryLargerRadius') || 'Prova ad aumentare il raggio di ricerca'}</p>
                    </div>
                  ) : (
                    <div className="divide-y divide-[#262626]">
                      {filteredStations.map((station, index) => (
                        <div 
                          key={station.id}
                          className={`p-4 hover:bg-[#1a1a1a] cursor-pointer transition-colors ${
                            selectedStation?.id === station.id ? 'bg-[#FF5F1F]/10 border-l-2 border-[#FF5F1F]' : ''
                          }`}
                          onClick={() => setSelectedStation(station)}
                          data-testid={`station-${index}`}
                        >
                          <div className="flex items-start justify-between gap-3">
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2">
                                <h3 className="font-medium text-white truncate">{station.name}</h3>
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    toggleFavorite(station, 'OSM');
                                  }}
                                  className={`p-1 rounded transition-colors ${
                                    favoriteIds.has(station.id) 
                                      ? 'text-yellow-400 hover:text-yellow-300' 
                                      : 'text-[#6B7280] hover:text-yellow-400'
                                  }`}
                                  title={favoriteIds.has(station.id) ? 'Rimuovi dai preferiti' : 'Aggiungi ai preferiti'}
                                >
                                  <Star className={`w-4 h-4 ${favoriteIds.has(station.id) ? 'fill-current' : ''}`} />
                                </button>
                              </div>
                              {station.brand && station.brand !== station.name && (
                                <p className="text-sm text-[#FF5F1F]">{station.brand}</p>
                              )}
                              {station.address && (
                                <p className="text-xs text-[#9CA3AF] mt-1 truncate">{station.address}</p>
                              )}
                              {station.fuel_types && station.fuel_types.length > 0 && (
                                <div className="flex flex-wrap gap-1 mt-2">
                                  {station.fuel_types.slice(0, 3).map((type, idx) => (
                                    <span key={idx} className="px-1.5 py-0.5 bg-[#262626] text-[#9CA3AF] text-xs">
                                      {type}
                                    </span>
                                  ))}
                                </div>
                              )}
                            </div>
                            <div className="text-right flex flex-col items-end gap-2">
                              <span className="text-sm font-mono text-[#FF5F1F]">
                                {formatDistance(station.distance_m)}
                              </span>
                              <Button
                                size="sm"
                                variant="outline"
                                className="border-[#262626] hover:border-[#FF5F1F] hover:text-[#FF5F1F] px-2 py-1 h-auto"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  navigateToStation(station);
                                }}
                              >
                                <Navigation className="w-3 h-3 mr-1" />
                                <span className="text-xs">{t('fuelStations.navigate') || 'Vai'}</span>
                              </Button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="mt-4 p-3 bg-[#0a0a0a] border border-[#262626] text-xs text-[#9CA3AF]">
              {t('fuelStations.dataSource') || 'Dati forniti da OpenStreetMap.'}
            </div>
          </TabsContent>

          {/* Tab: Prices (DE, FR, IT) */}
          <TabsContent value="prices">
            <div className="flex justify-end mb-4">
              <Button
                onClick={searchPrices}
                disabled={loadingPrices || !userLocation}
                className="bg-[#FF5F1F] hover:bg-[#FF5F1F]/90 text-white"
                data-testid="search-prices-btn"
              >
                {loadingPrices ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <TrendingDown className="w-4 h-4 mr-2" />
                )}
                {t('fuelStations.searchPrices') || 'Cerca Prezzi'}
              </Button>
            </div>

            {/* Price Results */}
            <div className="bg-[#121212] border border-[#262626]">
              <div className="p-4 border-b border-[#262626] flex items-center justify-between">
                <h2 className="text-lg font-semibold text-white flex items-center gap-2">
                  <DollarSign className="w-5 h-5 text-[#FF5F1F]" />
                  {t('fuelStations.pricesResults') || 'Prezzi Carburante'}
                </h2>
                <div className="flex items-center gap-2">
                  <span className="text-sm text-[#9CA3AF]">
                    {priceStations.length} {t('fuelStations.stations') || 'stazioni'}
                  </span>
                  <div className="flex gap-1">
                    <span title="Germania">🇩🇪</span>
                    <span title="Francia">🇫🇷</span>
                    <span title="Italia">🇮🇹</span>
                  </div>
                </div>
              </div>

              <div className="max-h-[500px] overflow-y-auto">
                {!priceSearchPerformed ? (
                  <div className="p-8 text-center text-[#9CA3AF]">
                    <DollarSign className="w-12 h-12 mx-auto mb-4 opacity-50" />
                    <p>{t('fuelStations.pricesPrompt') || 'Cerca i prezzi carburante in tempo reale'}</p>
                    <p className="text-sm mt-2 text-[#6B7280]">
                      {t('fuelStations.pricesAvailable') || 'Disponibile per Germania, Francia e Italia'}
                    </p>
                  </div>
                ) : loadingPrices ? (
                  <div className="p-8 text-center">
                    <Loader2 className="w-8 h-8 mx-auto animate-spin text-[#FF5F1F]" />
                    <p className="mt-2 text-[#9CA3AF]">{t('fuelStations.searchingPrices') || 'Ricerca prezzi...'}</p>
                  </div>
                ) : priceStations.length === 0 ? (
                  <div className="p-8 text-center text-[#9CA3AF]">
                    <Fuel className="w-12 h-12 mx-auto mb-4 opacity-50" />
                    <p>{t('fuelStations.noPricesFound') || 'Nessun prezzo trovato'}</p>
                    <p className="text-sm mt-2">{t('fuelStations.pricesOnlyDEFRIT') || 'I prezzi sono disponibili solo per Germania, Francia e Italia'}</p>
                  </div>
                ) : (
                  <div className="divide-y divide-[#262626]">
                    {priceStations.map((station, index) => (
                      <div 
                        key={station.id}
                        className="p-4 hover:bg-[#1a1a1a] transition-colors"
                        data-testid={`price-station-${index}`}
                      >
                        <div className="flex items-start justify-between gap-3">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <span className="text-lg">{getCountryFlag(station.country)}</span>
                              <h3 className="font-medium text-white truncate">{station.name}</h3>
                              <button
                                onClick={() => toggleFavorite(station, station.country)}
                                className={`p-1 rounded transition-colors ${
                                  favoriteIds.has(station.id) 
                                    ? 'text-yellow-400 hover:text-yellow-300' 
                                    : 'text-[#6B7280] hover:text-yellow-400'
                                }`}
                                title={favoriteIds.has(station.id) ? 'Rimuovi dai preferiti' : 'Aggiungi ai preferiti'}
                              >
                                <Star className={`w-4 h-4 ${favoriteIds.has(station.id) ? 'fill-current' : ''}`} />
                              </button>
                            </div>
                            {station.brand && station.brand !== station.name && (
                              <p className="text-sm text-[#FF5F1F] ml-7">{station.brand}</p>
                            )}
                            {(station.address || station.city) && (
                              <p className="text-xs text-[#9CA3AF] mt-1 ml-7 truncate">
                                {station.address}{station.city && `, ${station.city}`}
                              </p>
                            )}
                            
                            {/* Prices */}
                            <div className="flex flex-wrap gap-2 mt-3 ml-7">
                              {station.prices.diesel && (
                                <div className="px-2 py-1 bg-[#262626] rounded">
                                  <span className="text-xs text-[#9CA3AF]">Diesel</span>
                                  <p className="text-sm font-mono text-green-400">{formatPrice(station.prices.diesel)}</p>
                                </div>
                              )}
                              {station.prices.sp95 && (
                                <div className="px-2 py-1 bg-[#262626] rounded">
                                  <span className="text-xs text-[#9CA3AF]">SP95</span>
                                  <p className="text-sm font-mono text-green-400">{formatPrice(station.prices.sp95)}</p>
                                </div>
                              )}
                              {station.prices.sp98 && (
                                <div className="px-2 py-1 bg-[#262626] rounded">
                                  <span className="text-xs text-[#9CA3AF]">SP98</span>
                                  <p className="text-sm font-mono text-green-400">{formatPrice(station.prices.sp98)}</p>
                                </div>
                              )}
                              {station.prices.e10 && (
                                <div className="px-2 py-1 bg-[#262626] rounded">
                                  <span className="text-xs text-[#9CA3AF]">E10</span>
                                  <p className="text-sm font-mono text-green-400">{formatPrice(station.prices.e10)}</p>
                                </div>
                              )}
                              {station.prices.gpl && (
                                <div className="px-2 py-1 bg-[#262626] rounded">
                                  <span className="text-xs text-[#9CA3AF]">GPL</span>
                                  <p className="text-sm font-mono text-green-400">{formatPrice(station.prices.gpl)}</p>
                                </div>
                              )}
                              {station.prices.metano && (
                                <div className="px-2 py-1 bg-[#262626] rounded">
                                  <span className="text-xs text-[#9CA3AF]">Metano</span>
                                  <p className="text-sm font-mono text-green-400">{formatPrice(station.prices.metano)}</p>
                                </div>
                              )}
                            </div>

                            {station.last_update && (
                              <p className="text-xs text-[#6B7280] mt-2 ml-7 flex items-center gap-1">
                                <Clock className="w-3 h-3" />
                                {new Date(station.last_update).toLocaleString('it-IT')}
                              </p>
                            )}
                          </div>
                          <div className="text-right flex flex-col items-end gap-2">
                            <span className="text-sm font-mono text-[#FF5F1F]">
                              {formatDistance(station.distance_m)}
                            </span>
                            <Button
                              size="sm"
                              variant="outline"
                              className="border-[#262626] hover:border-[#FF5F1F] hover:text-[#FF5F1F] px-2 py-1 h-auto"
                              onClick={() => navigateToStation(station)}
                            >
                              <Navigation className="w-3 h-3 mr-1" />
                              <span className="text-xs">{t('fuelStations.navigate') || 'Vai'}</span>
                            </Button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Sources info */}
            <div className="mt-4 p-3 bg-[#0a0a0a] border border-[#262626] text-xs text-[#9CA3AF]">
              <p className="flex items-center gap-2">
                <Flag className="w-4 h-4 text-[#FF5F1F]" />
                {t('fuelStations.pricesSources') || 'Fonti: prix-carburants.gouv.fr (Francia), MISE Osservaprezzi (Italia), Tankerkoenig (Germania)'}
              </p>
            </div>
          </TabsContent>

          {/* Tab: Favorites */}
          <TabsContent value="favorites">
            <div className="bg-[#121212] border border-[#262626]">
              <div className="p-4 border-b border-[#262626] flex items-center justify-between">
                <h2 className="text-lg font-semibold text-white flex items-center gap-2">
                  <Star className="w-5 h-5 text-[#FF5F1F]" />
                  {t('fuelStations.favorites') || 'Stazioni Preferite'}
                </h2>
                <span className="text-sm text-[#9CA3AF]">
                  {favoriteStations.length} {t('fuelStations.stations') || 'stazioni'}
                </span>
              </div>

              <div className="max-h-[500px] overflow-y-auto">
                {loadingFavorites ? (
                  <div className="p-8 text-center">
                    <Loader2 className="w-8 h-8 mx-auto animate-spin text-[#FF5F1F]" />
                    <p className="mt-2 text-[#9CA3AF]">{t('common.loading')}</p>
                  </div>
                ) : favoriteStations.length === 0 ? (
                  <div className="p-8 text-center text-[#9CA3AF]">
                    <Star className="w-12 h-12 mx-auto mb-4 opacity-50" />
                    <p>{t('fuelStations.noFavorites') || 'Nessuna stazione preferita'}</p>
                    <p className="text-sm mt-2">{t('fuelStations.addFavoritesHint') || 'Cerca stazioni e clicca sulla stella per aggiungerle ai preferiti'}</p>
                  </div>
                ) : (
                  <div className="divide-y divide-[#262626]">
                    {favoriteStations.map((station, index) => (
                      <div 
                        key={station.id}
                        className="p-4 hover:bg-[#1a1a1a] transition-colors"
                        data-testid={`favorite-station-${index}`}
                      >
                        <div className="flex items-start justify-between gap-3">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              {station.country !== 'OSM' && (
                                <span className="text-lg">{getCountryFlag(station.country)}</span>
                              )}
                              <h3 className="font-medium text-white truncate">{station.name}</h3>
                            </div>
                            {station.brand && station.brand !== station.name && (
                              <p className="text-sm text-[#FF5F1F]">{station.brand}</p>
                            )}
                            {(station.address || station.city) && (
                              <p className="text-xs text-[#9CA3AF] mt-1 truncate">
                                {station.address}{station.city && `, ${station.city}`}
                              </p>
                            )}
                          </div>
                          <div className="flex flex-col items-end gap-2">
                            <div className="flex items-center gap-2">
                              <Button
                                size="sm"
                                variant="outline"
                                className="border-[#262626] hover:border-[#FF5F1F] hover:text-[#FF5F1F] px-2 py-1 h-auto"
                                onClick={() => navigateToStation(station)}
                              >
                                <Navigation className="w-3 h-3 mr-1" />
                                <span className="text-xs">{t('fuelStations.navigate') || 'Vai'}</span>
                              </Button>
                              <Button
                                size="sm"
                                variant="ghost"
                                className="text-red-500 hover:text-red-400 hover:bg-red-500/10 px-2 py-1 h-auto"
                                onClick={() => toggleFavorite({ id: station.station_id, ...station })}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </Layout>
  );
}
